import {
  type TireInstallation,
  type TireInstallationQueryParams,
  type PerformanceLogDetail,
  type PerformanceLogQueryParams,
  type DownloadImageParams,
  type CreatePerformanceLogPayload,
  type PaginatedTireInstallations,
} from 'types/tire-installation.types';
import { type UninstallTireRequest } from 'types/tire-inventory.types';
import BaseAPI from './base.api';

export default class TireInstallationAPI extends BaseAPI {
  /**
   * Gets active tire installations
   * @param params Query parameters for filtering
   * @returns Active tire installation list
   */
  getActiveTireInstallations = async (
    params: TireInstallationQueryParams
  ): Promise<PaginatedTireInstallations | TireInstallation[]> => {
    const response = await this.API.get('tire-installation-log/active', {
      params: {
        customerId: params.customerId,
        ...(params.assetId && { assetId: params.assetId }),
        ...(params.position && { position: params.position }),
        ...(params.page && { page: params.page }),
        ...(params.limit && { limit: params.limit }),
      },
    });
    return response.data;
  };

  /**
   * Gets performance logs for a tire installation
   * @param params Query parameters with installationId and customerId
   * @returns Performance log list
   */
  getPerformanceLogs = async (
    params: PerformanceLogQueryParams
  ): Promise<PerformanceLogDetail[]> => {
    const response = await this.API.get(
      `tire-installation-log/${params.installationId}/performance-logs`,
      {
        params: {
          customerId: params.customerId,
        },
      }
    );
    return response.data;
  };

  /**
   * Gets download URL for performance log image
   * @param params Query parameters with performanceLogId, customerId, and expiresIn
   * @returns Download URL
   */
  getPerformanceLogImageUrl = async (
    params: DownloadImageParams
  ): Promise<any> => {
    const response = await this.API.get(
      `tire-performance-log/${params.performanceLogId}/image`,
      {
        params: {
          customerId: params.customerId,
          expiresIn: params.expiresIn ?? 3600,
        },
      }
    );
    return response.data;
  };

  /**
   * Creates a new performance log entry
   * @param payload Performance log data with file upload
   * @returns Created performance log
   */
  createPerformanceLog = async (
    payload: CreatePerformanceLogPayload
  ): Promise<PerformanceLogDetail> => {
    const formData = new FormData();

    // Add all form fields
    formData.append('installationId', payload.installationId);
    formData.append('assetId', payload.assetId);
    formData.append('timestamp', payload.timestamp);
    formData.append('measuredAirPressure', payload.measuredAirPressure);
    formData.append('averageTreadDepth', payload.averageTreadDepth);
    formData.append('totalDistanceLog', payload.totalDistanceLog);
    formData.append('totalEngineHoursLog', payload.totalEngineHoursLog);
    formData.append('damageReported', payload.damageReported.toString());
    formData.append('sideWallDamage', payload.sideWallDamage.toString());
    formData.append('unevenTreadWear', payload.unevenTreadWear.toString());
    formData.append('currentTireCondition', payload.currentTireCondition);
    formData.append('inspectionNotes', payload.inspectionNotes);

    // Add file if provided
    if (payload.inspectionImage) {
      formData.append('inspectionImage', payload.inspectionImage);
    }

    const response = await this.API.post('tire-performance-log', formData, {
      params: {
        customerId: payload.customerId,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  };

  /**
   * Uninstalls a tire from an asset
   * @param payload Uninstall tire data
   * @param customerId Customer ID
   * @returns Success response
   */
  uninstallTire = async (
    payload: UninstallTireRequest,
    customerId: string
  ): Promise<any> => {
    const response = await this.API.post(
      'tire-installation-log/uninstall',
      payload,
      {
        params: {
          customerId,
        },
      }
    );
    return response.data;
  };
}
